import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import StatusPill from "../components/StatusPill";
import {
  QrCode, CheckCircle, Warning, ArrowLeft, ArrowClockwise, Package as PackageIcon, AirplaneTilt,
} from "@phosphor-icons/react";

const CTX_LABEL = {
  PICKUP: "Pickup scan (sender premises)",
  SOURCE_RECEIPT: "Origin cargo-terminal receipt scan",
  DESTINATION_RECEIPT: "Destination airport receipt scan",
  DELIVERY: "Delivery scan",
};

export default function ScanPackage() {
  const { token } = useParams();
  const { profile } = useAuth();
  const nav = useNavigate();

  const [phase, setPhase] = useState("loading"); // loading | ready | invalid | unauthorised | error | success
  const [ctx, setCtx] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  const homePath = profile?.role === "RID" ? "/rider" : profile?.role === "CAP" ? "/captain" : "/app";

  const resolve = useCallback(async () => {
    setPhase("loading"); setErrMsg("");
    try {
      const { data } = await api.get(`/shipments/scan/resolve/${encodeURIComponent(token)}`);
      setCtx(data);
      setPhase("ready");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) { setPhase("invalid"); }
      else if (status === 403) { setPhase("unauthorised"); setErrMsg(e.displayMessage || "You are not authorised to scan this package."); }
      else { setPhase("error"); setErrMsg(e.displayMessage || "We couldn't load this. Please try again."); }
    }
  }, [token]);

  useEffect(() => { resolve(); }, [resolve]);

  const submitScan = async () => {
    if (!ctx) return;
    setScanning(true);
    try {
      let data;
      if (ctx.scan_channel === "rider") {
        ({ data } = await api.post(`/rider/shipments/${ctx.shipment_id}/pickup-scan`, { scan_token: token }));
        setResult({
          package_label: data.package_label,
          scanned_count: data.scanned,
          expected_count: data.expected,
          complete: data.scanned >= data.expected,
        });
      } else {
        ({ data } = await api.post(`/shipments/${ctx.shipment_id}/scan`, {
          scan_token: token, scan_context: ctx.expected_scan_action,
        }));
        setResult({
          package_label: data.package_label,
          scanned_count: data.scanned_count,
          expected_count: data.expected_count,
          complete: data.complete,
        });
      }
      setPhase("success");
    } catch (e) {
      // Backend remains authoritative — surface its validation message inline, stay on the page.
      setErrMsg(e.displayMessage || "Scan could not be completed.");
    } finally {
      setScanning(false);
    }
  };

  const Header = () => (
    <div className="flex items-center gap-3 mb-6" data-testid="scan-header">
      <div className="w-10 h-10 rounded-lg bg-[hsl(var(--foreground))] flex items-center justify-center">
        <QrCode size={22} weight="fill" className="text-white" />
      </div>
      <div>
        <div className="heading text-lg leading-tight">Air12</div>
        <div className="overline text-[hsl(var(--muted))]">PACKAGE SCAN</div>
      </div>
    </div>
  );

  const BackBtn = () => (
    <button onClick={() => nav(homePath)} className="btn-ghost text-xs" data-testid="scan-back-button">
      <ArrowLeft size={14} /> Back
    </button>
  );

  return (
    <div className="max-w-[560px] mx-auto p-4 sm:p-6" data-testid="scan-package-page">
      <Header />

      {phase === "loading" && (
        <div className="grid-card p-6 text-sm text-[hsl(var(--muted))]" data-testid="scan-loading">Resolving package…</div>
      )}

      {phase === "invalid" && (
        <div className="grid-card grid-card-strong p-6" data-testid="scan-invalid">
          <div className="flex items-center gap-2 text-[hsl(var(--destructive))]"><Warning size={20} weight="fill" /><span className="font-semibold">Invalid package code</span></div>
          <p className="text-sm text-[hsl(var(--muted))] mt-2">This QR code doesn’t match a known Air12 package. Please re-scan the printed label or contact Air12 Support.</p>
          <div className="mt-4"><BackBtn /></div>
        </div>
      )}

      {phase === "unauthorised" && (
        <div className="grid-card grid-card-strong p-6" data-testid="scan-unauthorised">
          <div className="flex items-center gap-2 text-[hsl(var(--warning))]"><Warning size={20} weight="fill" /><span className="font-semibold">Not authorised</span></div>
          <p className="text-sm text-[hsl(var(--muted))] mt-2">{errMsg}</p>
          <div className="mt-4"><BackBtn /></div>
        </div>
      )}

      {phase === "error" && (
        <div className="grid-card grid-card-strong p-6" data-testid="scan-error">
          <div className="flex items-center gap-2 text-[hsl(var(--destructive))]"><Warning size={20} weight="fill" /><span className="font-semibold">Something went wrong</span></div>
          <p className="text-sm text-[hsl(var(--muted))] mt-2">{errMsg}</p>
          <div className="mt-4 flex gap-2">
            <button className="btn-primary text-xs" onClick={resolve} data-testid="scan-retry-button"><ArrowClockwise size={14} /> Retry</button>
            <BackBtn />
          </div>
        </div>
      )}

      {phase === "ready" && ctx && (
        <div className="space-y-4" data-testid="scan-ready">
          <div className="grid-card grid-card-strong p-4">
            <div className="flex items-center justify-between">
              <span className="id-chip id-chip-strong" data-testid="scan-shipment-code">{ctx.shipment_code}</span>
              <StatusPill status={ctx.current_status} />
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <PackageIcon size={16} className="text-[hsl(var(--muted))]" />
              <span className="font-medium" data-testid="scan-package-label">{ctx.package_label}</span>
              <span className="text-[hsl(var(--muted))]">· {ctx.sequence_no}/{ctx.total_count_in_shipment}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-[hsl(var(--muted))]" data-testid="scan-route">
              <span>{ctx.source_airport}</span>
              <AirplaneTilt size={14} />
              <span>{ctx.destination_airport}</span>
            </div>
          </div>

          <div className="grid-card p-4">
            <div className="overline">REQUIRED SCAN ACTION</div>
            <div className="heading text-lg mt-1" data-testid="scan-expected-action">
              {CTX_LABEL[ctx.expected_scan_action] || ctx.expected_scan_action}
            </div>
            <div className="mono text-[11px] text-[hsl(var(--muted))] mt-1" data-testid="scan-progress">
              {ctx.scanned_count}/{ctx.expected_count} packages scanned for this stage
            </div>

            {ctx.already_scanned && (
              <div className="mt-3 flex items-center gap-2 rounded-md border border-[hsl(var(--success))] bg-[hsl(var(--success)/0.08)] px-3 py-2" data-testid="scan-already-scanned">
                <CheckCircle size={18} weight="fill" className="text-[hsl(var(--success))]" />
                <span className="text-sm">Package already scanned for this stage.</span>
              </div>
            )}

            {errMsg && (
              <div className="mt-3 text-sm text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))] rounded-md px-3 py-2" data-testid="scan-inline-error">{errMsg}</div>
            )}

            <button
              className="btn-primary w-full justify-center mt-4"
              disabled={scanning}
              onClick={submitScan}
              data-testid="scan-confirm-button"
            >
              <CheckCircle size={16} weight="bold" />
              {scanning ? "Scanning…" : ctx.already_scanned ? "Re-confirm scan" : "Confirm scan"}
            </button>
          </div>

          <BackBtn />
        </div>
      )}

      {phase === "success" && result && (
        <div className="grid-card grid-card-strong p-6" data-testid="scan-success">
          <div className="flex items-center gap-2 text-[hsl(var(--success))]"><CheckCircle size={22} weight="fill" /><span className="font-semibold">Scan recorded</span></div>
          <p className="text-sm mt-2"><span className="mono">{result.package_label}</span> scanned successfully.</p>
          <div className="mono text-2xl heading mt-3" data-testid="scan-success-count">{result.scanned_count}/{result.expected_count}</div>
          {result.complete && (
            <div className="mt-1 text-sm text-[hsl(var(--success))] flex items-center gap-1.5" data-testid="scan-success-complete">
              <CheckCircle size={14} weight="fill" /> All packages scanned for this stage
            </div>
          )}
          <div className="mt-5">
            <button onClick={() => nav(homePath)} className="btn-primary text-xs" data-testid="scan-success-done"><ArrowLeft size={14} /> Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
